import { lazy, Suspense } from "react";
import { Outlet, useSearchParams, useLocation } from "react-router-dom";
import Header from "@/components/Header";
const Footer = lazy(() => import("@/components/Footer"));
import { useAuth } from "@/contexts/AuthContext";
import { UnifiedBottomNav } from "@/components/shared/UnifiedBottomNav";
import { ROUTES } from "@/constants";
import { FOCUSED_LAYOUT_SOURCE_IDS } from "@/constants/config";
import { useRouteEntitySafe } from "@/contexts/RouteEntityContext";
import { MaintenanceBanner } from "@/components/shared/MaintenanceBanner";
import { PageViewTracker } from "@/components/PageViewTracker";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";

/**
 * Persistent layout wrapper for public pages
 * Prevents Header/Footer from remounting on navigation
 * Supports ?preview=true parameter for in-context editing (hides Header/Footer)
 * 
 * NOTE: No sidebar on public pages - sidebar is only for /chat, /account, /admin routes
 * NOTE: Suspense is handled at route level in App.tsx - no nested Suspense here
 */
export const PublicLayoutWrapper = () => {
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get('preview') === 'true';
  const { user } = useAuth();
  const location = useLocation();
  const routeEntity = useRouteEntitySafe();
  const isBillingFlow =
    [ROUTES.PLANS, ROUTES.CHECKOUT, ROUTES.CHECKOUT_CONFIRMATION]
      .some(route => location.pathname === route) ||
    (routeEntity?.currentEntity?.type === 'route' &&
     FOCUSED_LAYOUT_SOURCE_IDS.has(routeEntity.currentEntity.sourceId ?? ''));

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip">
      <PageViewTracker />
      <GoogleAnalytics />
      <MaintenanceBanner />
      {!isPreviewMode && <Header />}
      <main className={`flex-1 ${user ? 'pb-20 md:pb-0' : ''}`}>
        <Outlet />
      </main>
      {!isPreviewMode && !isBillingFlow && (
        <Suspense fallback={<div className="h-64" />}>
          <Footer />
        </Suspense>
      )}
      {user && <UnifiedBottomNav />}
    </div>
  );
};
